import { ref, computed, reactive } from "vue";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { useUserStore } from "@/stores/userStore";
import { useQuestionBank } from "@/composables/useQuestionBank";
import { useQuestionStore } from "@/stores/questionStore";
import { duePriorityScore, isSkillDue } from "@/utils/spacedRepetition";
import { useDailyStreakEngine } from "@/composables/useDailyStreakEngine";
import { isLessonLocked } from "@/composables/usePlanGating";

/**
 * Revision Quiz Engine
 * --------------------
 * Quiz-style revision: answer ALL questions first (no feedback),
 * then see comprehensive results. Supports skip + back/forward navigation.
 *
 * state:
 * - sessionStarted, sessionCompleted, sessionPaused
 * - queue, currentIndex, currentItem, currentQuestion
 * - results (keyed by index), skipped (Set)
 * - answeredCount, score, topicBreakdown, allQuestionsHandled
 * - skillFocus, displayDifficulty
 *
 * actions:
 * - start({ totalQuestions? })
 * - recordAnswer(result)
 * - skipQuestion()
 * - goToQuestion(index), goNext(), goBack()
 * - finishSession()
 * - reset()
 */

const BAND_DIFFICULTY_MAP = {
  beginner: "easy",
  developing: "medium",
  secure: "medium",
  confident: "hard",
  mastery: "hard"
};

export function useRevisionEngine(childId) {
  const progressStore = useProgressStore();
  const curriculumStore = useCurriculumStore();
  const userStore = useUserStore();
  const questionBank = useQuestionBank();
  const questionStore = useQuestionStore();
  const streakEngine = useDailyStreakEngine();

  // Build a Set of SG IDs that are locked for the current plan.
  // Uses allSectionGroups which already carries excludedPlans from the lesson.
  function lockedSGIds() {
    const plan = userStore.effectivePlan;
    return new Set(
      (curriculumStore.allSectionGroups || [])
        .filter(sg => isLessonLocked(sg.excludedPlans, plan))
        .map(sg => sg.sectionGroupId)
    );
  }

  const DEFAULT_TOTAL_QUESTIONS = 10;

  const sessionStarted = ref(false);
  const sessionCompleted = ref(false);
  const sessionPaused = ref(false);
  const _starting = ref(false);

  const totalQuestions = ref(DEFAULT_TOTAL_QUESTIONS);

  const queue = ref([]); // [{ sectionGroupId, difficulty, question }]
  const currentIndex = ref(0);
  const startMs = ref(0);

  const skillFocus = ref([]); // selected sgIds for this session

  // Quiz results: keyed by queue index
  const results = reactive({}); // { [index]: { questionId, sectionGroupId, difficulty, correct, question, containers } }
  const skipped = reactive(new Set()); // indices of skipped questions

  const currentItem = computed(() => queue.value[currentIndex.value] || null);
  const currentQuestion = computed(() => currentItem.value?.question || null);
  const currentSectionGroupId = computed(() => currentItem.value?.sectionGroupId || null);

  const displayDifficulty = computed(() => currentItem.value?.difficulty || "medium");

  const skillsCount = ref(0);

  // Quiz computeds
  const answeredCount = computed(() => Object.keys(results).length);

  const score = computed(() => {
    const entries = Object.values(results);
    const correct = entries.filter(r => r.correct).length;
    // Total should be full queue length (skipped questions count as wrong)
    const total = queue.value.length;
    const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { correct, total, percent };
  });

  const topicBreakdown = computed(() => {
    const breakdown = {};
    for (const r of Object.values(results)) {
      const sgId = r.sectionGroupId;
      if (!breakdown[sgId]) breakdown[sgId] = { correct: 0, total: 0 };
      breakdown[sgId].total += 1;
      if (r.correct) breakdown[sgId].correct += 1;
    }
    return breakdown;
  });

  const allQuestionsHandled = computed(() => {
    const len = queue.value.length;
    if (len === 0) return false;
    for (let i = 0; i < len; i++) {
      if (!(i in results) && !skipped.has(i)) return false;
    }
    return true;
  });

  const isCurrentAnswered = computed(() => currentIndex.value in results);
  const isCurrentSkipped = computed(() => skipped.has(currentIndex.value));

  function dueRevisionCount(cid) {
    const progress = progressStore.getChildProgress(cid.value || cid);
    if (!progress) return 0;

    const now = Date.now();
    return Object.values(progress).filter(p =>
      isSkillDue({
        lastAttempt: p.lastAttempt,
        accuracy: p.accuracy,
        now
      })
    ).length;
  }

  function clampDifficulty(d) {
    const x = (d || "").toString().toLowerCase();
    if (x === "easy" || x === "medium" || x === "hard") return x;
    return "medium";
  }

  function bandToDifficulty(band) {
    return BAND_DIFFICULTY_MAP[band] || "medium";
  }

  /**
   * Choose which section groups to revise (reused from original)
   */
  function selectSectionGroupsForRevision({ limit = 6 } = {}) {
    progressStore.ensureChild(childId);

    const prog = progressStore.getChildProgress(childId) || {};
    const now = Date.now();
    const locked = lockedSGIds();

    const entries = Object.entries(prog)
      .filter(([sgId]) => sgId && !locked.has(sgId)) // skip empty keys + locked SGs
      .map(([sgId, p]) => {
        const acc = typeof p.accuracy === "number" ? p.accuracy : 0.45;
        const lastAttempt = p.lastAttempt || 0;
        const due = isSkillDue({ lastAttempt, accuracy: acc, now });
        const pScore = duePriorityScore({ lastAttempt, accuracy: acc, now });
        return { sgId, acc, lastAttempt, due, score: pScore };
      });

    const dueList = entries
      .filter(e => e.due)
      .sort((a, b) => b.score - a.score);

    const notDueList = entries
      .filter(e => !e.due)
      .sort((a, b) => (a.lastAttempt || 0) - (b.lastAttempt || 0));

    const picked = [];

    for (const e of dueList) {
      if (picked.length >= limit) break;
      picked.push(e.sgId);
    }

    for (const e of notDueList) {
      if (picked.length >= limit) break;
      picked.push(e.sgId);
    }

    return picked;
  }

  /**
   * Get a question from the prefetched cache only (no API calls).
   * Tries target difficulty first, then falls back to adjacent difficulties.
   */
  function getQuestionFromCache(sgId, difficulty) {
    const DIFFICULTIES = ["easy", "medium", "hard"];
    const target = clampDifficulty(difficulty);
    const idx = DIFFICULTIES.indexOf(target);

    // Build fallback order: target first, then adjacent, then remaining
    const order = [target];
    if (idx - 1 >= 0) order.push(DIFFICULTIES[idx - 1]);
    if (idx + 1 < DIFFICULTIES.length) order.push(DIFFICULTIES[idx + 1]);
    for (const d of DIFFICULTIES) {
      if (!order.includes(d)) order.push(d);
    }

    for (const diff of order) {
      const q = questionStore.getFromCache(sgId, diff);
      if (q) return q;
    }
    return null;
  }

  async function buildQueue() {
    const sgIds = selectSectionGroupsForRevision({ limit: 6 });
    skillFocus.value = sgIds;

    // Single batch API call — fetches stratified questions for all sgIds
    await questionBank.prefetch(sgIds, { perGroup: 15 });

    if (!sgIds.length) {
      queue.value = [];
      currentIndex.value = 0;
      return;
    }

    const q = [];
    let robin = 0;
    let consecutiveMisses = 0;

    while (q.length < totalQuestions.value && consecutiveMisses < sgIds.length) {
      const sgId = sgIds[robin % sgIds.length];
      robin++;

      const band = progressStore.getSectionGroupBand(childId, sgId);
      const difficulty = bandToDifficulty(band);
      const question = getQuestionFromCache(sgId, difficulty);
      if (!question) {
        consecutiveMisses++;
        continue;
      }
      consecutiveMisses = 0;
      q.push({ sectionGroupId: sgId, difficulty, question });
    }

    // Shuffle so it feels mixed
    q.sort(() => Math.random() - 0.5);

    queue.value = q;
    currentIndex.value = 0;
  }

  async function start({ totalQuestions: tq } = {}) {
    if (_starting.value) return;
    _starting.value = true;

    try {
      // Wait for progress to load from API before selecting revision topics
      if (!progressStore.isLoaded(childId)) {
        await progressStore.loadProgress(childId);
      }
      progressStore.ensureChild(childId);
      startMs.value = Date.now();

      if (typeof tq === "number" && tq >= 5 && tq <= 30) {
        totalQuestions.value = tq;
      } else {
        totalQuestions.value = DEFAULT_TOTAL_QUESTIONS;
      }

      // Reset session
      sessionStarted.value = true;
      sessionCompleted.value = false;
      sessionPaused.value = false;

      // Clear results and skipped
      for (const key in results) delete results[key];
      skipped.clear();

      questionBank.resetSession();
      skillsCount.value = dueRevisionCount(childId);

      await buildQueue();

      if (!queue.value.length) {
        sessionCompleted.value = true;
      }
    } finally {
      _starting.value = false;
    }
  }

  function pause() {
    sessionPaused.value = true;
  }

  function resume() {
    sessionPaused.value = false;
  }

  function reset() {
    sessionStarted.value = false;
    sessionCompleted.value = false;
    sessionPaused.value = false;
    queue.value = [];
    currentIndex.value = 0;
    skillFocus.value = [];
    for (const key in results) delete results[key];
    skipped.clear();
    questionBank.resetSession();
  }

  /**
   * Find the next unanswered/unskipped question index, or -1 if none.
   * Searches forward from startIdx, wrapping around.
   */
  function findNextUnanswered(startIdx) {
    const len = queue.value.length;
    for (let offset = 0; offset < len; offset++) {
      const idx = (startIdx + offset) % len;
      if (!(idx in results) && !skipped.has(idx)) return idx;
    }
    return -1;
  }

  /**
   * Record the answer for the current question.
   * result: { questionId, formatSectionGroups, difficulty, correct, containers }
   */
  function recordAnswer(result) {
    const idx = currentIndex.value;
    const item = queue.value[idx];
    if (!item) return;

    // Don't record if this question is already answered or was skipped
    if (idx in results || skipped.has(idx)) return;

    const sgId = item.sectionGroupId;

    // Store result
    results[idx] = {
      questionId: result.questionId,
      sectionGroupId: sgId,
      difficulty: item.difficulty,
      correct: !!result.correct,
      question: item.question,
      containers: result.containers || []
    };

    // Remove from skipped if it was previously skipped
    skipped.delete(idx);

    // Auto-advance to next unanswered question
    const nextIdx = findNextUnanswered(idx + 1);
    if (nextIdx >= 0) {
      currentIndex.value = nextIdx;
    }
    // If all handled, stay on current (UI will show Finish button)
  }

  function skipQuestion() {
    const idx = currentIndex.value;
    if (idx in results) return; // can't skip an answered question

    skipped.add(idx);

    // Advance to next unanswered/unskipped question
    const nextIdx = findNextUnanswered(idx + 1);
    if (nextIdx >= 0) {
      currentIndex.value = nextIdx;
    }
  }

  function goToQuestion(index) {
    if (index >= 0 && index < queue.value.length) {
      currentIndex.value = index;
    }
  }

  function goNext() {
    if (currentIndex.value < queue.value.length - 1) {
      currentIndex.value += 1;
    }
  }

  function goBack() {
    if (currentIndex.value > 0) {
      currentIndex.value -= 1;
    }
  }

  function finishSession() {
    if (sessionCompleted.value) return; // already saved
    const sc = score.value;

    // Batch-update progress for all answered questions
    for (const r of Object.values(results)) {
      if (r.sectionGroupId) {
        progressStore.updateProgress(childId, r.sectionGroupId, r.correct);
      }
    }

    streakEngine.registerActivity(childId);

    progressStore.addActivity(childId, {
      type: "revision",
      title: "Revision quiz complete",
      score: sc.percent
    });

    progressStore.addRevisionHistory(childId, {
      date: new Date().toISOString(),
      score: sc.percent,
      questionsTotal: sc.total + skipped.size,
      questionsCorrect: sc.correct,
      topics: { ...topicBreakdown.value }
    });

    progressStore.awardXP(childId, 15, "revision_complete");
    progressStore.flushLessonProgress(childId);

    sessionCompleted.value = true;
  }

  return {
    // state
    sessionStarted,
    sessionCompleted,
    sessionPaused,
    totalQuestions,
    queue,
    currentIndex,
    currentItem,
    currentQuestion,
    currentSectionGroupId,
    displayDifficulty,
    skillFocus,
    results,
    skipped,

    // computeds
    answeredCount,
    score,
    topicBreakdown,
    allQuestionsHandled,
    isCurrentAnswered,
    isCurrentSkipped,

    // actions
    start,
    recordAnswer,
    skipQuestion,
    goToQuestion,
    goNext,
    goBack,
    finishSession,
    pause,
    resume,
    reset,
    dueRevisionCount
  };
}
